/**
 * Reference-counted scroll lock for the mobile drawer, chat panel, and
 * any other surface that overlays the page on small viewports.
 *
 * Multiple callers may `lockScroll()` concurrently (e.g. the AppSidebar
 * drawer + the chat panel briefly overlap during transitions). We track
 * lock depth and only release the original styles when the last caller
 * unlocks, so each consumer can safely call lock/unlock in pairs without
 * worrying about ordering.
 *
 * We also pin the html element's overflow (in addition to body) because
 * iOS Safari otherwise rubber-bands the document underneath a fixed
 * overlay. Original styles are captured on the first lock and restored
 * on the last unlock, so we never trample inline styles set elsewhere.
 */
type SavedStyles = {
  bodyHeight: string;
  bodyOverflow: string;
  htmlOverflow: string;
};

let lockDepth = 0;
let saved: SavedStyles | null = null;

export const setBodyScroll = (height: string, overflow: string) => {
  const body = document.querySelector('body');
  if (!body) return;
  body.style.height = height;
  body.style.overflow = overflow;
};

export const lockScroll = () => {
  if (typeof document === 'undefined') return;
  const body = document.body;
  const html = document.documentElement;
  if (!body || !html) return;

  if (lockDepth === 0) {
    saved = {
      bodyHeight: body.style.height,
      bodyOverflow: body.style.overflow,
      htmlOverflow: html.style.overflow,
    };
    body.style.height = '100vh';
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
  }
  lockDepth += 1;
};

export const unlockScroll = () => {
  if (typeof document === 'undefined') return;
  if (lockDepth === 0) return;
  lockDepth -= 1;
  if (lockDepth > 0) return;

  const body = document.body;
  const html = document.documentElement;
  if (!body || !html) return;
  if (saved) {
    body.style.height = saved.bodyHeight;
    body.style.overflow = saved.bodyOverflow;
    html.style.overflow = saved.htmlOverflow;
    saved = null;
  } else {
    body.style.height = '';
    body.style.overflow = '';
    html.style.overflow = '';
  }
};
